import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";
import { cn } from "@/lib/utils";

function Sheet(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root {...props} />;
}

function SheetTrigger(props: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger {...props} />;
}

function SheetClose(props: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close {...props} />;
}

type SheetSide = "top" | "right" | "bottom" | "left";
const sideClasses: Record<SheetSide, string> = {
  top: "inset-x-0 top-0 w-full border-b",
  right: "inset-y-0 right-0 h-full w-[min(24rem,90vw)] border-l",
  bottom: "inset-x-0 bottom-0 w-full border-t",
  left: "inset-y-0 left-0 h-full w-[min(24rem,90vw)] border-r",
};

function SheetContent({
  side = "right",
  className,
  children,
  onCloseAutoFocus,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & { side?: SheetSide }) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-foreground/50" />
      <SheetContentSurface
        side={side}
        className={className}
        onCloseAutoFocus={onCloseAutoFocus}
        {...props}
      >
        {children}
      </SheetContentSurface>
    </DialogPrimitive.Portal>
  );
}

/**
 * Sheets open from shell state rather than a `SheetTrigger`, and Radix only restores focus to a
 * trigger. Remember what had focus when the sheet mounted so closing it returns focus there.
 */
function SheetContentSurface({
  side = "right",
  className,
  children,
  onCloseAutoFocus,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & { side?: SheetSide }) {
  const [previouslyFocused] = React.useState<HTMLElement | null>(() =>
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );

  return (
    <DialogPrimitive.Content
      className={cn(
        "fixed z-50 overflow-y-auto bg-popover p-6 text-popover-foreground shadow-dialog outline-none",
        sideClasses[side],
        className,
      )}
      onCloseAutoFocus={(event) => {
        onCloseAutoFocus?.(event);
        if (event.defaultPrevented) return;
        if (!previouslyFocused?.isConnected) return;
        event.preventDefault();
        previouslyFocused.focus();
      }}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  );
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title className={cn("text-lg font-semibold", className)} {...props} />;
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle, SheetTrigger };
